
import { useState, useEffect } from "react";
import { VacationChat } from "./VacationChat";
import { ResultsHeader } from "./ResultsHeader";
import { ResultsFilters } from "./ResultsFilters";
import { ResultsGrid } from "./ResultsGrid";
import { trackSearchInteraction, updateSearchPerformance } from "@/utils/searchTracking";

interface VacationResultsProps {
  vacations: any[];
  searchQuery: string;
  onBack: () => void;
  onSearch: (query: string) => void;
  isBroadSearch?: boolean;
  message?: string;
  loading?: boolean;
}

export const VacationResults = ({
  vacations,
  searchQuery,
  onBack,
  onSearch,
  isBroadSearch = false,
  message,
  loading = false
}: VacationResultsProps) => {
  const [displayCount, setDisplayCount] = useState(20);
  const [filteredVacations, setFilteredVacations] = useState(vacations);
  const [isFiltered, setIsFiltered] = useState(false);
  const [hasTrackedSearch, setHasTrackedSearch] = useState(false);
  
  // Reset state when vacations or searchQuery changes
  useEffect(() => {
    console.log('Resetting vacation results state due to new vacations or search query');
    setFilteredVacations(vacations);
    setIsFiltered(false);
    setDisplayCount(20);
    setHasTrackedSearch(false);
  }, [vacations, searchQuery]);

  // Track search interaction when component loads with results
  useEffect(() => {
    if (!hasTrackedSearch && vacations.length > 0 && searchQuery) {
      trackSearchInteraction({
        interactionType: 'search',
        searchQuery,
        resultsCount: vacations.length
      });
      
      updateSearchPerformance(searchQuery, vacations.length, false);
      setHasTrackedSearch(true);
    }
  }, [vacations, searchQuery, hasTrackedSearch]);
  
  const displayedVacations = filteredVacations.slice(0, displayCount);
  const hasMoreResults = filteredVacations.length > displayCount;

  // Extract location info for more personalized messaging
  const getLocationFromQuery = (query: string) => {
    const lowerQuery = query.toLowerCase();
    if (lowerQuery.includes('parijs')) return 'Parijs';
    if (lowerQuery.includes('frankrijk')) return 'Frankrijk';
    if (lowerQuery.includes('spanje')) return 'Spanje';
    if (lowerQuery.includes('italië') || lowerQuery.includes('italie')) return 'Italië';
    if (lowerQuery.includes('griekenland')) return 'Griekenland';
    if (lowerQuery.includes('turkije')) return 'Turkije';
    return null;
  };

  const location = getLocationFromQuery(searchQuery);
  const vacationCount = filteredVacations.length;

  const getPersonalizedMessage = () => {
    if (isBroadSearch) {
      return "Geen exacte match? Geen probleem! Zonnie heeft deze alternatieven voor je uitgezocht ✨";
    }
    
    // Handle zero results case
    if (vacationCount === 0) {
      if (location) {
        return `Helaas heeft Zonnie geen geschikte vakanties gevonden in ${location}. Geen zorgen: wij breiden ons aanbod elke dag uit! Probeer eens andere filters of zoektermen 🌟`;
      }
      return `Helaas heeft Zonnie geen geschikte vakanties gevonden voor jouw zoekopdracht. Geen zorgen: wij breiden ons aanbod elke dag uit! Probeer eens andere filters of zoektermen ✨`;
    }
    
    if (location) {
      return `Goed nieuws! Zonnie heeft ${vacationCount} geweldige ${vacationCount === 1 ? 'vakantie' : 'vakanties'} voor jou gevonden in ${location} 🌟`;
    }
    
    return `Fantastisch! Zonnie heeft ${vacationCount} perfecte ${vacationCount === 1 ? 'vakantie' : 'vakanties'} voor jou uitgezocht 🎉`;
  };

  const filtersLogic = ResultsFilters({ 
    vacations, 
    filteredVacations, 
    isFiltered, 
    onFilteredVacationsChange: setFilteredVacations, 
    onIsFilteredChange: setIsFiltered, 
    onDisplayCountChange: setDisplayCount 
  });

  const handleAiRefineSearch = (refinement: string) => {
    console.log('Performing AI-powered refinement search:', refinement);
    // Combine original search query with refinement for AI search
    const combinedQuery = `${searchQuery} ${refinement}`;
    onSearch(combinedQuery);
  };

  const handleResetFilters = () => {
    console.log('Resetting filters');
    filtersLogic.resetFilters();
  };

  return (
    <div className="space-y-6">
      <ResultsHeader 
        onBack={onBack}
        personalizedMessage={getPersonalizedMessage()}
      />

      <VacationChat 
        searchQuery={searchQuery}
        vacationCount={vacationCount}
        onAiRefineSearch={handleAiRefineSearch}
        onResetFilters={handleResetFilters}
        isFiltered={isFiltered}
      />
      
      {isBroadSearch && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <p className="text-yellow-800">
            💡 Tip: Gebruik de zoekbalk hierboven om je resultaten verder te verfijnen!
          </p>
        </div>
      )}
      
      <ResultsGrid 
        displayedVacations={displayedVacations}
        filteredVacations={filteredVacations}
        displayCount={displayCount}
        hasMoreResults={hasMoreResults}
        isFiltered={isFiltered}
        vacationCount={vacationCount}
        loading={loading}
        searchQuery={searchQuery}
        onDisplayCountChange={setDisplayCount}
        onResetFilters={handleResetFilters}
      />
    </div>
  );
};
